import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ShieldCheck,
  FileText,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Building2,
  RefreshCw,
  Download,
  ChevronRight,
  Loader2
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../api';
import toast from 'react-hot-toast';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

const markdownComponents = {
  h1: ({ children }: any) => (
    <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-4 pb-2 border-b-2 border-indigo-200">{children}</h1>
  ),
  h2: ({ children }: any) => (
    <h2 className="text-xl font-bold text-gray-900 mt-6 mb-3 pb-1 border-b border-gray-200">{children}</h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="text-lg font-semibold text-gray-900 mt-5 mb-2">{children}</h3>
  ),
  h4: ({ children }: any) => (
    <h4 className="text-base font-semibold text-gray-800 mt-4 mb-2">{children}</h4>
  ),
  p: ({ children }: any) => (
    <p className="text-gray-700 text-sm mb-3 leading-relaxed">{children}</p>
  ),
  strong: ({ children }: any) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }: any) => <em className="italic text-gray-600">{children}</em>,
  ul: ({ children }: any) => (
    <ul className="space-y-1 my-3 ml-5 list-disc">{children}</ul>
  ),
  ol: ({ children }: any) => (
    <ol className="space-y-1 my-3 ml-5 list-decimal">{children}</ol>
  ),
  li: ({ children }: any) => (
    <li className="text-gray-700 text-sm leading-relaxed">{children}</li>
  ),
  code: ({ children, className }: any) => {
    const isBlock = !!className;
    return isBlock ? (
      <pre className="bg-gray-900 text-green-300 text-xs font-mono p-4 rounded-lg overflow-x-auto my-3 leading-relaxed whitespace-pre-wrap">
        <code>{children}</code>
      </pre>
    ) : (
      <code className="bg-gray-100 text-indigo-700 px-1.5 py-0.5 rounded text-xs font-mono">
        {children}
      </code>
    );
  },
  table: ({ children }: any) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-gray-200">
      <table className="w-full text-sm border-collapse" style={{ minWidth: 'max-content' }}>{children}</table>
    </div>
  ),
  thead: ({ children }: any) => <thead className="bg-gray-50">{children}</thead>,
  tbody: ({ children }: any) => <tbody>{children}</tbody>,
  tr: ({ children }: any) => <tr className="border-b border-gray-200 last:border-0">{children}</tr>,
  th: ({ children }: any) => (
    <th className="px-3 py-2 text-left font-semibold text-gray-900 text-xs uppercase tracking-wider align-top whitespace-normal" style={{ minWidth: '8rem' }}>{children}</th>
  ),
  td: ({ children }: any) => (
    <td className="px-3 py-2 text-gray-700 text-xs align-top whitespace-normal" style={{ minWidth: '8rem' }}>{children}</td>
  ),
  blockquote: ({ children }: any) => (
    <blockquote className="border-l-4 border-indigo-300 bg-indigo-50/50 pl-4 pr-3 py-2 my-3 text-gray-700 italic text-sm">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-gray-200" />,
  a: ({ href, children }: any) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
    >
      {children}
    </a>
  )
};

type Status = 'Met' | 'Substantially Met' | 'Partial' | 'Roadmap' | 'Not Applicable';

interface Subcategory {
  id: string;
  title: string;
  status: Status;
  evidence_doc?: string;
  evidence_code?: string;
  owner?: string;
  cadence?: string;
  target?: string;
}

interface FunctionGroup {
  id: string;
  label: string;
  description: string;
  subcategories: Subcategory[];
}

interface Matrix {
  framework: string;
  version: string;
  last_reviewed: string;
  owner: string;
  functions: FunctionGroup[];
}

interface ModuleMetric {
  module: string;
  run_count: number;
  avg_confidence: number;
  avg_failure_rate: number;
  human_review_count: number;
  human_review_rate: number;
  avg_latency_ms: number;
  total_token_overhead: number;
}

interface VerificationMetrics {
  available: boolean;
  reason?: string;
  window_days?: number;
  scope?: string;
  totals?: {
    run_count: number;
    avg_confidence: number;
    avg_failure_rate: number;
    human_review_rate: number;
    avg_latency_ms: number;
  };
  per_module?: ModuleMetric[];
}

interface AuditMetrics {
  available: boolean;
  reason?: string;
  totals?: {
    event_count: number;
    export_eligible_count: number;
    export_eligible_rate: number;
    override_count: number;
    override_rate_on_low_band: number;
  };
  by_band?: Array<{ band: string; event_count: number }>;
}

interface VendorMetrics {
  available: boolean;
  reason?: string;
  totals?: {
    vendor_count: number;
    soc2_expiring_within_90d: number;
    dpa_expiring_within_90d: number;
    open_alerts: number;
  };
}

interface ConfigurationRow {
  id: number;
  scope: string;
  tenant_id: number | null;
  module_name: string | null;
  threshold_high: number;
  threshold_moderate: number;
  weight_self_verification: number | string;
  weight_citation_verification: number | string;
  hard_block_export: boolean;
  soft_warn_export: boolean;
  calibrated_at: string | null;
  calibration_sample_size: number | null;
  updated_at: string;
}

interface DocumentEntry {
  name: string;
  size_bytes: number;
  modified_at: string;
}

interface OverviewResponse {
  framework: string;
  version: string;
  last_reviewed: string;
  owner: string;
  scope: string;
  generated_at: string;
  summary: {
    counts: Record<string, number>;
    perFunction: Record<string, Record<string, number>>;
    roadmap: Array<{
      function: string;
      id: string;
      title: string;
      status: Status;
      target: string | null;
      owner: string | null;
    }>;
  };
  runtime: {
    verification: VerificationMetrics;
    audit: AuditMetrics;
    vendor: VendorMetrics;
    configuration: { available: boolean; reason?: string; rows?: ConfigurationRow[] };
  };
  documents: DocumentEntry[];
}

const STATUS_STYLES: Record<Status, string> = {
  Met: 'bg-green-100 text-green-800 border-green-200',
  'Substantially Met': 'bg-blue-100 text-blue-800 border-blue-200',
  Partial: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Roadmap: 'bg-purple-100 text-purple-800 border-purple-200',
  'Not Applicable': 'bg-gray-100 text-gray-700 border-gray-200'
};

function formatPercent(n: number | undefined, digits: number = 1): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(digits)}%`;
}

function formatNumber(n: number | undefined): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

interface ComplianceDashboardProps {
  embedded?: boolean;
}

const ComplianceDashboard: React.FC<ComplianceDashboardProps> = ({ embedded = false }) => {
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [windowDays, setWindowDays] = useState<number>(30);
  const [loading, setLoading] = useState(false);
  const [activeFunction, setActiveFunction] = useState<string>('GOVERN');
  const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
  const [docContent, setDocContent] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docHighlight, setDocHighlight] = useState(false);
  const documentSectionRef = useRef<HTMLDivElement | null>(null);
  const documentBodyRef = useRef<HTMLDivElement | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, matrixRes] = await Promise.all([
        api.get('/api/ai-compliance/overview', { params: { windowDays } }),
        api.get('/api/ai-compliance/matrix')
      ]);
      setOverview(overviewRes.data.data);
      setMatrix(matrixRes.data.data);
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || 'Failed to load compliance data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const loadDocument = useCallback(async (name: string, options: { scroll?: boolean } = {}) => {
    setDocLoading(true);
    setSelectedDoc(name);
    setDocContent(null);
    if (options.scroll && documentSectionRef.current) {
      documentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setDocHighlight(true);
      window.setTimeout(() => setDocHighlight(false), 1600);
    }
    try {
      const res = await api.get(`/api/ai-compliance/documents/${encodeURIComponent(name)}`, {
        responseType: 'text',
        transformResponse: [(data: any) => data]
      });
      setDocContent(typeof res.data === 'string' ? res.data : String(res.data));
      if (documentBodyRef.current) {
        documentBodyRef.current.scrollTop = 0;
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err.message || 'Failed to load document';
      toast.error(message);
      setSelectedDoc(null);
    } finally {
      setDocLoading(false);
    }
  }, []);

  const handleExportMatrix = useCallback(() => {
    if (!matrix) return;
    const blob = new Blob([JSON.stringify(matrix, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nist-ai-rmf-matrix-${matrix.last_reviewed}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [matrix]);

  const summary = overview?.summary;
  const runtime = overview?.runtime;
  const counts = summary?.counts;

  const overallProgress = useMemo(() => {
    if (!counts || !counts.total) return 0;
    const credited = (counts['Met'] || 0) + (counts['Substantially Met'] || 0) * 0.6;
    const denominator = counts.total - (counts['Not Applicable'] || 0);
    if (denominator <= 0) return 0;
    return Math.round((credited / denominator) * 100);
  }, [counts]);

  const activeGroup = useMemo(() => {
    if (!matrix) return null;
    return matrix.functions.find(f => f.id === activeFunction) || matrix.functions[0];
  }, [matrix, activeFunction]);

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-900">AI Compliance Dashboard</h1>
              <AdminSecurityHelpButton screenId="ai-compliance" size="sm" />
            </div>
            <p className="text-gray-600">
              {matrix
                ? `${matrix.framework} • v${matrix.version} • Last reviewed ${matrix.last_reviewed} • Owner: ${matrix.owner}`
                : 'Loading…'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              disabled={loading}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>
            <button
              onClick={loadAll}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </button>
            <button
              onClick={handleExportMatrix}
              disabled={!matrix}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export matrix
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overall coverage</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{overallProgress}%</p>
                <p className="text-xs text-gray-500 mt-1">
                  {counts?.Met || 0} Met • {counts?.['Substantially Met'] || 0} Substantial • {counts?.Roadmap || 0} Roadmap
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-50 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
            <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
              <div className="bg-indigo-600 h-2 rounded-full transition-all" style={{ width: `${overallProgress}%` }} />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Verification runs</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {formatNumber(runtime?.verification?.totals?.run_count)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Avg confidence {formatNumber(runtime?.verification?.totals?.avg_confidence)} •
                  Latency {formatNumber(runtime?.verification?.totals?.avg_latency_ms)}ms
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">{`Window: last ${windowDays}d`}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Export gate</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {formatPercent(runtime?.audit?.totals?.export_eligible_rate)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Override rate (low band): {formatPercent(runtime?.audit?.totals?.override_rate_on_low_band)}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              {formatNumber(runtime?.audit?.totals?.event_count)} gate events
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Vendor health</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {formatNumber(runtime?.vendor?.totals?.vendor_count)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  SOC2 expiring &le;90d: {formatNumber(runtime?.vendor?.totals?.soc2_expiring_within_90d)}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              {formatNumber(runtime?.vendor?.totals?.open_alerts)} open vendor alerts
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">NIST AI RMF — Subcategory matrix</h2>
            {summary && (
              <div className="flex items-center gap-2 text-xs">
                {(['Met', 'Substantially Met', 'Partial', 'Roadmap', 'Not Applicable'] as Status[]).map(s => (
                  <span key={s} className={`px-2 py-1 rounded-full border ${STATUS_STYLES[s]}`}>
                    {s}: {counts?.[s] || 0}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="border-b border-gray-200 bg-gray-50 px-6 py-3 flex flex-wrap gap-2">
            {matrix?.functions.map(fn => {
              const fnCounts = summary?.perFunction?.[fn.id] || {};
              const fnTotal = fnCounts.total || 0;
              const fnMet = (fnCounts.Met || 0) + (fnCounts['Substantially Met'] || 0);
              return (
                <button
                  key={fn.id}
                  onClick={() => setActiveFunction(fn.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeFunction === fn.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {fn.label}
                  <span className="ml-2 text-xs opacity-80">{fnMet}/{fnTotal}</span>
                </button>
              );
            })}
          </div>

          {activeGroup && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subcategory</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cadence</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Evidence</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeGroup.subcategories.map(sub => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-mono text-gray-900 whitespace-nowrap">{sub.id}</td>
                      <td className="px-6 py-3 text-sm text-gray-700">
                        <div className="max-w-xl">{sub.title}</div>
                        {sub.target && (
                          <div className="text-xs text-purple-700 mt-1">Target: {sub.target}</div>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[sub.status]}`}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{sub.owner || '—'}</td>
                      <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{sub.cadence || '—'}</td>
                      <td className="px-6 py-3 text-sm">
                        {sub.evidence_doc && (
                          <button
                            onClick={() => {
                              const docName = sub.evidence_doc!.split('#')[0];
                              if (docName.endsWith('.md') && !docName.startsWith('..')) {
                                loadDocument(docName, { scroll: true });
                              } else {
                                toast(`External reference: ${sub.evidence_doc}`, { icon: '🔗' });
                              }
                            }}
                            className="inline-flex items-center text-indigo-600 hover:text-indigo-800 text-xs hover:underline"
                          >
                            <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="break-all">{sub.evidence_doc}</span>
                          </button>
                        )}
                        {sub.evidence_code && (
                          <div className="text-xs font-mono text-gray-500 mt-1 break-all">
                            {sub.evidence_code}
                          </div>
                        )}
                        {!sub.evidence_doc && !sub.evidence_code && '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow lg:col-span-2">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Verification activity by subsystem</h2>
              <p className="text-xs text-gray-500 mt-1">Live from `verification_traces` over the selected window.</p>
            </div>
            <div className="overflow-x-auto">
              {runtime?.verification?.available && runtime.verification.per_module && runtime.verification.per_module.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Runs</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg score</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Failure rate</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Human review</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Latency</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {runtime.verification.per_module.map(m => (
                      <tr key={m.module} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{m.module}</td>
                        <td className="px-6 py-3 text-sm text-gray-700 text-right">{formatNumber(m.run_count)}</td>
                        <td className="px-6 py-3 text-sm text-gray-700 text-right">{formatNumber(m.avg_confidence)}</td>
                        <td className="px-6 py-3 text-sm text-gray-700 text-right">{formatPercent(m.avg_failure_rate)}</td>
                        <td className="px-6 py-3 text-sm text-gray-700 text-right">{formatPercent(m.human_review_rate)}</td>
                        <td className="px-6 py-3 text-sm text-gray-700 text-right">{formatNumber(m.avg_latency_ms)}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-sm text-gray-500">
                  {runtime?.verification?.available
                    ? `No verification runs in the last ${windowDays} days for the current scope.`
                    : runtime?.verification?.reason || 'Verification metrics unavailable.'}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Roadmap items</h2>
              <p className="text-xs text-gray-500 mt-1">Open close-out targets.</p>
            </div>
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {summary?.roadmap && summary.roadmap.length > 0 ? (
                summary.roadmap.map(item => (
                  <div key={item.id} className="px-6 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-mono text-gray-500">{item.function} · {item.id}</p>
                        <p className="text-sm text-gray-900 mt-0.5">{item.title}</p>
                        {item.owner && <p className="text-xs text-gray-500 mt-1">Owner: {item.owner}</p>}
                      </div>
                      {item.target && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 whitespace-nowrap">
                          {item.target}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-sm text-gray-500">No open roadmap items.</div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Active verification configuration</h2>
              <p className="text-xs text-gray-500 mt-1">Snapshot from `verification_configurations` (governance-controlled).</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            {runtime?.configuration?.available && runtime.configuration.rows && runtime.configuration.rows.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scope</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">High</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Moderate</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Self vs Citation weight</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hard block</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calibrated at</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {runtime.configuration.rows.map(row => (
                    <tr key={row.id}>
                      <td className="px-6 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {row.scope}
                        {row.tenant_id && <span className="text-xs text-gray-500 ml-1">(t:{row.tenant_id})</span>}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">{row.module_name || '—'}</td>
                      <td className="px-6 py-3 text-sm text-gray-700 text-right">{row.threshold_high}</td>
                      <td className="px-6 py-3 text-sm text-gray-700 text-right">{row.threshold_moderate}</td>
                      <td className="px-6 py-3 text-sm text-gray-700 text-right">
                        {Number(row.weight_self_verification).toFixed(2)} / {Number(row.weight_citation_verification).toFixed(2)}
                      </td>
                      <td className="px-6 py-3 text-sm">
                        {row.hard_block_export ? (
                          <span className="inline-flex items-center text-green-700 text-xs">
                            <CheckCircle2 className="w-3 h-3 mr-1" />on
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-amber-700 text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />off
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(row.calibrated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-sm text-gray-500">
                {runtime?.configuration?.reason || 'Using static defaults — no rows in `verification_configurations`.'}
              </div>
            )}
          </div>
        </div>

        <div
          ref={documentSectionRef}
          className={`bg-white rounded-lg shadow scroll-mt-6 transition-shadow duration-500 ${
            docHighlight ? 'ring-2 ring-indigo-500 ring-offset-2' : ''
          }`}
        >
          <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Compliance documents</h2>
              <p className="text-xs text-gray-500 mt-1">Markdown sources behind every claim in this dashboard.</p>
            </div>
            {selectedDoc && (
              <span className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                <FileText className="w-3 h-3 mr-1.5" />
                {selectedDoc}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
            <div className="md:col-span-1 max-h-[640px] overflow-y-auto bg-gray-50/50">
              {overview?.documents && overview.documents.length > 0 ? (
                overview.documents.map(doc => (
                  <button
                    key={doc.name}
                    onClick={() => loadDocument(doc.name)}
                    className={`w-full text-left px-5 py-3 flex items-center justify-between transition-colors ${
                      selectedDoc === doc.name
                        ? 'bg-indigo-50 border-l-4 border-indigo-600'
                        : 'border-l-4 border-transparent hover:bg-white'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${selectedDoc === doc.name ? 'text-indigo-900' : 'text-gray-900'}`}>
                        {doc.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {Math.round(doc.size_bytes / 1024)} KB • {formatDate(doc.modified_at)}
                      </p>
                    </div>
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 ${selectedDoc === doc.name ? 'text-indigo-600' : 'text-gray-400'}`} />
                  </button>
                ))
              ) : (
                <div className="p-6 text-sm text-gray-500">No documents found.</div>
              )}
            </div>
            <div ref={documentBodyRef} className="md:col-span-2 p-8 max-h-[640px] overflow-y-auto bg-white">
              {docLoading ? (
                <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Loading {selectedDoc}…
                </div>
              ) : selectedDoc && docContent ? (
                <article className="prose prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {docContent}
                  </ReactMarkdown>
                </article>
              ) : (
                <div className="text-center py-16">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">Select a document on the left, or click an evidence link in the matrix above to view it here.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {overview && (
          <p className="text-center text-xs text-gray-400 mt-8">
            Generated {formatDate(overview.generated_at)} • Scope: {overview.scope}
          </p>
        )}
      </div>
    </div>
  );
};

export default ComplianceDashboard;
